import { CaseSizeRuleCreateRequestType } from "@sellar-io/api-v2/types";
import { useForm, useStore } from "@tanstack/react-form";
import { TanStackFormField } from "@sellar-io/components/adapters";
import {
  Form,
  InputNumber,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { useEffect } from "react";
import { CustomerBanner } from "../../../alerts/CustomerBanner";
import { generateProductItemName } from "../../../helpers/generateProductItemName";
import { useSellarSupplier } from "../../../../../hooks/useSellarSupplier";
import {
  createPackageRuleFormSchema,
  getCreatePackageRuleFormValues,
} from "./CreatePackageRuleForm.shared";

export type CreatePackageRuleFormInputs = CaseSizeRuleCreateRequestType;
type CreatePackageRuleFormValues = {
  containerType: CreatePackageRuleFormInputs["containerType"];
  unit: CreatePackageRuleFormInputs["unit"];
  volume?: number;
  quantity?: number;
};

export const CreatePackageRuleForm = ({
  formId,
  initialValues,
  onSubmit,
}: {
  formId?: string;
  initialValues?: CreatePackageRuleFormInputs;
  onSubmit: (data: CreatePackageRuleFormInputs) => void;
}) => {
  const form = useForm({
    defaultValues: getCreatePackageRuleFormValues(
      initialValues,
    ) as CreatePackageRuleFormValues,
    onSubmit: ({ value }) => {
      onSubmit(createPackageRuleFormSchema.parse(value));
    },
  });
  const supplier = useSellarSupplier();
  const { containerType, volume, unit, quantity } = useStore(
    form.store,
    (state) => state.values,
  );

  useEffect(() => {
    const values = getCreatePackageRuleFormValues(initialValues);

    form.setFieldValue("containerType", values.containerType);
    form.setFieldValue("unit", values.unit);
    form.setFieldValue("volume", values.volume);
    form.setFieldValue("quantity", values.quantity);
  }, [form, initialValues]);

  const canShowCustomerAdvice =
    containerType !== undefined &&
    volume !== undefined &&
    unit !== undefined &&
    quantity !== undefined;
  const productItemName = canShowCustomerAdvice
    ? generateProductItemName({
        containerType,
        volume,
        unit,
      })
    : null;

  return (
    <Form id={formId} onSubmit={form.handleSubmit}>
      <TanStackFormField
        form={form}
        name="containerType"
        label="Container type"
      >
        {({ value, setValue, inputProps }) => (
          <InputSelect
            {...inputProps}
            value={value}
            onChange={(event) =>
              setValue(
                event.target
                  .value as CreatePackageRuleFormInputs["containerType"],
              )
            }
            required
          >
            <InputOption value={"bottle"}>Bottle</InputOption>
            <InputOption value={"can"}>Can</InputOption>
          </InputSelect>
        )}
      </TanStackFormField>

      <TanStackFormField form={form} name="volume" label="Volume">
        {({ numberInputProps }) => (
          <InputNumber {...numberInputProps} min={1} required />
        )}
      </TanStackFormField>

      <TanStackFormField form={form} name="unit" label="Unit">
        {({ value, setValue, inputProps }) => (
          <InputSelect
            {...inputProps}
            value={value}
            onChange={(event) =>
              setValue(
                event.target.value as CreatePackageRuleFormInputs["unit"],
              )
            }
            required
          >
            <InputOption value={"gal"}>gal - gallon</InputOption>
            <InputOption value={"L"}>L - litre</InputOption>
            <InputOption value={"ml"}>ml - millilitre</InputOption>
            <InputOption value={"cl"}>cl - centilitre</InputOption>
            <InputOption value={"pt"}>pt - pint</InputOption>
            <InputOption value={"oz"}>oz - ounce</InputOption>
            <InputOption value={"g"}>g - gram</InputOption>
            <InputOption value={"kg"}>kg - kilogram</InputOption>
          </InputSelect>
        )}
      </TanStackFormField>

      <TanStackFormField form={form} name="quantity" label="Quantity">
        {({ numberInputProps }) => (
          <InputNumber {...numberInputProps} step={"1"} min={1} required />
        )}
      </TanStackFormField>

      {canShowCustomerAdvice && productItemName ? (
        <div className="my-4">
          <CustomerBanner
            title={`Your customers will be ${supplier.enforceCaseSizeRules ? "required" : "advised"} to order ${productItemName} in quantities of ${quantity}`}
          />
        </div>
      ) : null}
    </Form>
  );
};
